/**
 * useGoalRewardIntegration — Connects goal completion events to reward pledge fulfillment.
 */
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';

export function useGoalRewardIntegration() {
  const { toast } = useToast();

  const linkRewardToTodo = useCallback(async (
    pledgeId: string, 
    todoId?: string, 
    goalId?: string
  ) => {
    if (!todoId && !goalId) return null;

    try {
      if (todoId) {
        const { data, error } = await supabase
          .from('todos')
          .update({
            reward_pledge_id: pledgeId,
            is_reward_linked: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', todoId)
          .select()
          .single();

        if (error) throw error;
        return data;
      }

      if (goalId) {
        const { data: goal } = await supabase
          .from('goals')
          .select('title, user_id')
          .eq('id', goalId)
          .single();

        if (!goal) throw new Error('Goal not found');

        const { data, error } = await supabase
          .from('todos')
          .insert({
            goal_id: goalId,
            user_id: goal.user_id,
            text: 'Complete reward challenge',
            reward_pledge_id: pledgeId,
            is_reward_linked: true,
            completed: false
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }

      return null;
    } catch (error: any) {
      logger.error('Error linking reward to todo:', error);
      toast({
        title: "Error linking reward",
        description: error.message,
        variant: "destructive"
      });
      return null;
    }
  }, [toast]);

  const unlinkRewardFromTodo = useCallback(async (todoId: string) => {
    try {
      const { error } = await supabase
        .from('todos')
        .update({
          reward_pledge_id: null,
          is_reward_linked: false,
          proof_submitted_at: null,
          proof_reviewed_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', todoId);

      if (error) throw error;
    } catch (error: any) {
      logger.error('Error unlinking reward from todo:', error);
      toast({
        title: "Error unlinking reward",
        description: error.message,
        variant: "destructive"
      });
    }
  }, [toast]);

  const markProofSubmitted = useCallback(async (todoId: string) => {
    try {
      const { error } = await supabase
        .from('todos')
        .update({
          proof_submitted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', todoId);

      if (error) throw error;
    } catch (error: any) {
      logger.error('Error marking proof submitted:', error);
    }
  }, []);

  const markProofReviewed = useCallback(async (todoId: string) => {
    try {
      const { error } = await supabase
        .from('todos')
        .update({
          proof_reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', todoId);

      if (error) throw error;
    } catch (error: any) {
      logger.error('Error marking proof reviewed:', error);
    }
  }, []);

  return {
    linkRewardToTodo,
    unlinkRewardFromTodo,
    markProofSubmitted,
    markProofReviewed
  };
}
